<template>
  <div
    v-if="isReady"
    class="app-global-bg app-glass-preview"
    :class="{
      'app-global-bg--legacy': !useSvgBackground,
      'app-global-bg--svg': useSvgBackground,
      'app-global-bg--light': !isDarkTheme,
    }"
    :style="{ '--app-global-bg-image': effectiveBackgroundImage }"
  >
    <div v-if="useSvgBackground" class="app-global-bg__watermark">
      <img
        alt=""
        class="app-global-bg__watermark-image"
        :src="watermarkImageUrl"
      />
    </div>

    <v-snackbar-queue
      :closable="notificationClosable"
      :close-on-content-click="notificationCloseOnContentClick"
      :collapsed="notificationCollapsed"
      :location="snackbarLocation"
      :model-value="notificationQueue"
      :timeout="notificationTimeout"
      :total-visible="notificationTotalVisible"
      :prepend-icon="computedNotificationIcon"
      @update:model-value="handleNotificationQueueUpdate"
    />
    <confirmation-dialog
      v-model:show="confirmationShow"
      :cancel-color="confirmationCancelColor"
      :cancel-text="confirmationCancelText"
      :color="confirmationColor"
      :confirm-disabled="confirmationConfirmDisabled"
      :confirm-text="confirmationConfirmText"
      :icon="confirmationIcon"
      :loading="confirmationLoading"
      :message="confirmationMessage"
      :title="confirmationTitle"
      :warn-text="confirmationWarnText"
      @cancel="onCancelConfirmation"
      @confirm="onConfirmConfirmation"
    />

    <router-view v-slot="{ Component, route }">
      <component
        :is="Component"
        v-if="route.meta.layout === false && Component"
        :key="route.path"
      />

      <v-layout v-else-if="Component && isAuthenticated" class="fill-height">
        <AppHeader
          :menu-items="headerMenuItems"
          :on-toggle-full-screen="onToggleFullScreen"
          :on-toggle-theme="onToggleTheme"
          :show-menu="!showBottomNav"
          @menu-action="emit('menu-action', $event)"
          @ui-error="emit('ui-error', $event)"
        />

        <SideNav v-if="showSideNav" :options="sideNavOptions" />

        <v-main>
          <v-container fluid>
            <OfflineBanner
              v-if="showOfflineBanner"
              :use-alert="offlineBannerUseAlert"
            />
            <component :is="Component" :key="route.path" />
          </v-container>
        </v-main>

        <BottomNav v-if="showBottomNav" />
        <AppFooter v-if="!showBottomNav">
          <template #links>
            <slot name="footer-links" />
          </template>
        </AppFooter>
      </v-layout>
    </router-view>
  </div>

  <div
    v-else
    class="app-global-bg app-glass-preview"
    :class="{
      'app-global-bg--legacy': !useSvgBackground,
      'app-global-bg--svg': useSvgBackground,
      'app-global-bg--light': !isDarkTheme,
    }"
    :style="{ '--app-global-bg-image': effectiveBackgroundImage }"
  >
    <div v-if="useSvgBackground" class="app-global-bg__watermark">
      <img
        alt=""
        class="app-global-bg__watermark-image"
        :src="watermarkImageUrl"
      />
    </div>

    <v-app>
      <v-container
        class="d-flex flex-column align-center justify-center text-center fill-height"
        fluid
      >
        <v-progress-circular indeterminate />
      </v-container>
    </v-app>
  </div>
</template>

<script setup>
  import {
    useConfirmationState,
    useNotificationState,
  } from '#xapp-core/foundation'
  import { computed } from 'vue'
  import { useTheme } from 'vuetify'

  import bgCenteredUrl from '../../assets/x-bg-centered.png'
  import logoBlackUrl from '../../assets/x-logo-black.svg'
  import logoWhiteUrl from '../../assets/x-logo-white.svg'
  import OfflineBanner from '../OfflineBanner.vue'
  import AppFooter from './AppFooter.vue'
  import AppHeader from './AppHeader.vue'
  import BottomNav from './BottomNav.vue'
  import SideNav from './SideNav.vue'

  const props = defineProps({
    appGlobalBackgroundImage: {
      default: '',
      type: String,
    },
    headerMenuItems: {
      default: () => [],
      type: Array,
    },
    isAuthenticated: {
      default: false,
      type: Boolean,
    },
    isReady: {
      required: true,
      type: Boolean,
    },
    notificationClosable: {
      default: true,
      type: Boolean,
    },
    notificationCloseOnContentClick: {
      default: true,
      type: Boolean,
    },
    notificationCollapsed: {
      default: true,
      type: Boolean,
    },
    notificationTimeout: {
      default: 3000,
      type: Number,
    },
    notificationTotalVisible: {
      default: 5,
      type: Number,
    },
    offlineBannerUseAlert: {
      default: true,
      type: Boolean,
    },
    onToggleFullScreen: {
      default: null,
      type: Function,
    },
    onToggleTheme: {
      default: null,
      type: Function,
    },
    showBottomNav: {
      default: false,
      type: Boolean,
    },
    showOfflineBanner: {
      default: true,
      type: Boolean,
    },
    showSideNav: {
      default: false,
      type: Boolean,
    },
    sideNavOptions: {
      default: () => [],
      type: Array,
    },
    snackbarLocation: {
      default: 'top center',
      type: String,
    },
    useSvgBackground: {
      default: false,
      type: Boolean,
    },
  })

  const emit = defineEmits(['menu-action', 'ui-error'])
  const theme = useTheme()
  const { notificationQueue } = useNotificationState()
  const confirmationState = useConfirmationState()
  const confirmationShow = confirmationState.show
  const confirmationCancelColor = confirmationState.cancelColor
  const confirmationCancelText = confirmationState.cancelText
  const confirmationColor = confirmationState.color
  const confirmationConfirmDisabled = confirmationState.confirmDisabled
  const confirmationConfirmText = confirmationState.confirmText
  const confirmationIcon = confirmationState.icon
  const confirmationLoading = confirmationState.loading
  const confirmationMessage = confirmationState.message
  const confirmationTitle = confirmationState.title
  const confirmationWarnText = confirmationState.warnText

  // Map notification types to icons
  const ICON_MAP = new Map([
    ['error', 'mdi-close-circle'],
    ['info', 'mdi-information'],
    ['success', 'mdi-check-circle'],
    ['warning', 'mdi-alert'],
  ])

  // Compute the icon based on the first notification in the queue (if any)
  const computedNotificationIcon = computed(() => {
    if (
      Array.isArray(notificationQueue.value) &&
      notificationQueue.value.length > 0
    ) {
      const type = notificationQueue.value[0]?.type || 'info'
      return ICON_MAP.get(type) || 'mdi-information'
    }
    return 'mdi-information'
  })

  function handleNotificationQueueUpdate(nextQueue) {
    notificationQueue.value = Array.isArray(nextQueue) ? nextQueue : []
  }
  function onCancelConfirmation() {
    confirmationState.onCancel()
  }
  function onConfirmConfirmation() {
    confirmationState.onConfirm()
  }
  const isDarkTheme = computed(() => !!theme.global.current.value.dark)
  const useSvgBackground = computed(() => Boolean(props.useSvgBackground))
  const defaultBackgroundImage = computed(() =>
    useSvgBackground.value
      ? isDarkTheme.value
        ? logoWhiteUrl
        : logoBlackUrl
      : bgCenteredUrl
  )
  const watermarkImageUrl = computed(() => defaultBackgroundImage.value)
  const effectiveBackgroundImage = computed(
    () =>
      props.appGlobalBackgroundImage || `url(${defaultBackgroundImage.value})`
  )
</script>
